import { useCallback, useMemo, useState } from 'react';
import { getPremiacaoConsolidado } from '@spark/services/premiacao';
import { notification } from '@spark/ui-components/utils/notification';
import {
  premiacaoConsolidadoColumnFilterKeys,
  premiacaoConsolidadoMultiSelectFilterKeys
} from '../config/premiacaoConsolidadoFilterConfig';
import { matchesMultiSelectExact } from '../utils/premiacaoMultiSelectFilters';

const pick = (data, ...keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (data?.[key] !== undefined && data?.[key] !== null) {
      return data[key];
    }
  }
  return undefined;
};

const getDefaultPeriodo = () => {
  const now = new Date();
  return {
    mesFechamento: now.getMonth() + 1,
    anoFechamento: now.getFullYear()
  };
};

const parsePeriodoNumber = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const hasPeriodoInformado = (periodo) => {
  const mesFechamento = parsePeriodoNumber(periodo?.mesFechamento);
  const anoFechamento = parsePeriodoNumber(periodo?.anoFechamento);
  return mesFechamento !== null && anoFechamento !== null;
};

const toRowModel = (item, index) => ({
  id: `${pick(item, 'cpfMotorista', 'CpfMotorista') || 'row'}-${index}`,
  frotaCavalo: pick(item, 'frotaCavalo', 'FrotaCavalo') || '',
  cpfMotorista: pick(item, 'cpfMotorista', 'CpfMotorista') || '',
  motorista: pick(item, 'motorista', 'Motorista') || '',
  mesFechamento: pick(item, 'mesFechamento', 'MesFechamento'),
  anoFechamento: pick(item, 'anoFechamento', 'AnoFechamento'),
  kmRodado: pick(item, 'kmRodado', 'KmRodado'),
  litros: pick(item, 'litros', 'Litros'),
  media: pick(item, 'media', 'Media'),
  valorTotal: pick(item, 'valorTotal', 'ValorTotal'),
  faturamentoGeralMotorista: pick(item, 'faturamentoGeralMotorista', 'FaturamentoGeralMotorista'),
  kmRodadoGeralSegmentoMotorista: pick(item, 'kmRodadoGeralSegmentoMotorista', 'KmRodadoGeralSegmentoMotorista'),
  litrosGeralSegmentoMotorista: pick(item, 'litrosGeralSegmentoMotorista', 'LitrosGeralSegmentoMotorista'),
  mediaGeralSegmentoMotorista: pick(item, 'mediaGeralSegmentoMotorista', 'MediaGeralSegmentoMotorista'),
  valorPremiacaoTabelaPreco: pick(item, 'valorPremiacaoTabelaPreco', 'ValorPremiacaoTabelaPreco'),
  porcentagemDescontoInfracao: pick(item, 'porcentagemDescontoInfracao', 'PorcentagemDescontoInfracao'),
  descricaoInfracoes: pick(item, 'descricaoInfracoes', 'DescricaoInfracoes') || '',
  quantidadeInfracoes: pick(item, 'quantidadeInfracoes', 'QuantidadeInfracoes'),
  valorPremiacaoAntesInfracao: pick(item, 'valorPremiacaoAntesInfracao', 'ValorPremiacaoAntesInfracao'),
  valorPremiacao: pick(item, 'valorPremiacao', 'ValorPremiacao'),
  porcentagemPremiacao: pick(item, 'porcentagemPremiacao', 'PorcentagemPremiacao') || '',
  valorPremiacaoExcelenteTabelaPreco: pick(item, 'valorPremiacaoExcelenteTabelaPreco', 'ValorPremiacaoExcelenteTabelaPreco'),
  nomeTabelaPremiacao: pick(item, 'nomeTabelaPremiacao', 'NomeTabelaPremiacao') || '',
  segmentoTabelaPremiacao: pick(item, 'segmentoTabelaPremiacao', 'SegmentoTabelaPremiacao') || '',
  identificadorTabelaPremiacao: pick(item, 'identificadorTabelaPremiacao', 'IdentificadorTabelaPremiacao'),
  mediaIdealTabelaPremiacao: pick(item, 'mediaIdealTabelaPremiacao', 'MediaIdealTabelaPremiacao'),
  mediaExcelenteTabelaPremiacao: pick(item, 'mediaExcelenteTabelaPremiacao', 'MediaExcelenteTabelaPremiacao'),
  porcentagemDescontoTabelaPremiacao: pick(item, 'porcentagemDescontoTabelaPremiacao', 'PorcentagemDescontoTabelaPremiacao'),
  valorPremiacaoExcelenteTabelaPremiacaoAplicado: pick(item, 'valorPremiacaoExcelenteTabelaPremiacaoAplicado', 'ValorPremiacaoExcelenteTabelaPremiacaoAplicado'),
  valorInicioFaturamentoTabelaPremiacao: pick(item, 'valorInicioFaturamentoTabelaPremiacao', 'ValorInicioFaturamentoTabelaPremiacao'),
  valorFinalFaturamentoTabelaPremiacao: pick(item, 'valorFinalFaturamentoTabelaPremiacao', 'ValorFinalFaturamentoTabelaPremiacao'),
  dataCriacaoTabelaPremiacao: pick(item, 'dataCriacaoTabelaPremiacao', 'DataCriacaoTabelaPremiacao'),
  usuarioCriacaoTabelaPremiacao: pick(item, 'usuarioCriacaoTabelaPremiacao', 'UsuarioCriacaoTabelaPremiacao') || ''
});

const matchesTextFilter = (value, filterValue) => {
  if (filterValue === undefined || filterValue === null || filterValue === '') {
    return true;
  }

  return String(value ?? '').toLowerCase().includes(String(filterValue).toLowerCase());
};

const matchesColumnFilter = (value, filterValue, key) => {
  if (premiacaoConsolidadoMultiSelectFilterKeys.includes(key)) {
    return matchesMultiSelectExact(value, filterValue);
  }
  return matchesTextFilter(value, filterValue);
};

const filterRows = (rows, filtros) => (
  rows.filter((row) => (
    premiacaoConsolidadoColumnFilterKeys.every((key) => matchesColumnFilter(row[key], filtros[key], key))
  ))
);

export const usePremiacaoConsolidadoConsulta = () => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState(getDefaultPeriodo);
  const [periodo, setPeriodo] = useState({});
  const [consultaRealizada, setConsultaRealizada] = useState(false);

  const carregarConsolidado = useCallback(async (mesFechamento, anoFechamento) => {
    const mes = parsePeriodoNumber(mesFechamento);
    const ano = parsePeriodoNumber(anoFechamento);

    if (mes === null || ano === null) {
      notification.warning('Informe mês e ano de fechamento para consultar.', 'Período obrigatório');
      return;
    }

    if (mes < 1 || mes > 12) {
      notification.warning('Mês de fechamento deve estar entre 1 e 12.', 'Período inválido');
      return;
    }

    try {
      setLoading(true);
      const response = await getPremiacaoConsolidado({
        mesFechamento: mes,
        anoFechamento: ano
      });
      const lista = Array.isArray(response) ? response : [];
      setRegistros(lista.map(toRowModel));
      setConsultaRealizada(true);
    } catch (error) {
      console.error('Erro ao carregar premiacao consolidada:', error);
      notification.error(
        error?.message || 'Não foi possível carregar a premiação consolidada',
        'Erro ao consultar premiação'
      );
      setRegistros([]);
      setConsultaRealizada(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFiltrosChange = useCallback((novosFiltros) => {
    const mesFechamento = parsePeriodoNumber(novosFiltros.mesFechamento ?? periodo.mesFechamento);
    const anoFechamento = parsePeriodoNumber(novosFiltros.anoFechamento ?? periodo.anoFechamento);

    setFiltros({
      ...novosFiltros,
      mesFechamento: novosFiltros.mesFechamento ?? periodo.mesFechamento,
      anoFechamento: novosFiltros.anoFechamento ?? periodo.anoFechamento
    });

    if (mesFechamento === null || anoFechamento === null) {
      notification.warning('Informe mês e ano de fechamento para consultar.', 'Período obrigatório');
      return;
    }

    const periodoAlterado = mesFechamento !== periodo.mesFechamento
      || anoFechamento !== periodo.anoFechamento;

    const nextPeriod = { mesFechamento, anoFechamento };
    setPeriodo(nextPeriod);

    if (periodoAlterado || !consultaRealizada) {
      carregarConsolidado(mesFechamento, anoFechamento);
    }
  }, [carregarConsolidado, consultaRealizada, periodo.anoFechamento, periodo.mesFechamento]);

  const rows = useMemo(() => filterRows(registros, filtros), [registros, filtros]);

  const reload = useCallback(() => {
    if (!hasPeriodoInformado(periodo)) {
      notification.warning('Informe mês e ano de fechamento para consultar.', 'Período obrigatório');
      return;
    }

    carregarConsolidado(periodo.mesFechamento, periodo.anoFechamento);
  }, [carregarConsolidado, periodo]);

  const resetConsulta = useCallback(() => {
    const nextDefaultPeriodo = getDefaultPeriodo();
    setRegistros([]);
    setFiltros(nextDefaultPeriodo);
    setPeriodo({});
    setConsultaRealizada(false);
    setLoading(false);
  }, []);

  return {
    rows,
    loading,
    filtros,
    consultaRealizada,
    totalRegistros: rows.length,
    handleFiltrosChange,
    reload,
    resetConsulta
  };
};
